import {
  formatAppIntlLabel,
  getAppDisplayTimezone,
  getAppTodayIso,
} from "../utils/appDateTime";
import { getBrowserDeviceGuid } from "../utils/browserDeviceId";

const MANAGE_CONTEXT_KEY = "manageContext";
const QR_CACHE_KEY = "lastQrData";
const MANAGE_SELECTED_LOCATIONS_KEY = "manageSelectedLocations";
const DEFAULT_EMAIL = "";

function safeParse(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function formatDateLabel(dateValue) {
  if (!dateValue) return "Today";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Today";
  return formatAppIntlLabel(date, {
    month: "short",
    day: "numeric",
    timeZone: getAppDisplayTimezone(),
  });
}

function buildFromParts({ qrData = null, clientInfo = null, userName = "User", fallback = {} } = {}) {
  const clientDetails = qrData?.clientDetails || fallback.clientDetails || {};
  const shiftDetails = qrData?.shiftDetails || fallback.shiftDetails || {};
  const timesheetDetails = qrData?.timesheetDetails || fallback.timesheetDetails || {};

  const startDate =
    fallback.startDate ||
    fallback.shiftDate ||
    shiftDetails.startDate ||
    timesheetDetails.startDate ||
    clientInfo?.startDate ||
    null;

  const endDate =
    fallback.endDate ||
    shiftDetails.endDate ||
    timesheetDetails.endDate ||
    clientInfo?.endDate ||
    null;

  return {
    accountId: fallback.accountId || null,
    clientDeviceId: fallback.clientDeviceId || null,
    browserDeviceGuid: fallback.browserDeviceGuid || getBrowserDeviceGuid(),
    selectedLocationIds: Array.isArray(fallback.selectedLocationIds)
      ? fallback.selectedLocationIds
      : [],
    selectedLocationClientId:
      fallback.selectedLocationClientId ||
      (fallback.selectedLocationIds && fallback.selectedLocationIds.length > 0
        ? fallback.clientId || clientInfo?.clientId || clientDetails.clientId || clientDetails.client_id || null
        : null) ||
      null,
    contactRole: fallback.contactRole || "",
    capabilities: fallback.capabilities || null,
    permissions: fallback.permissions || [],
    roles: fallback.roles || [],
    clientId:
       fallback.clientId ||
       clientInfo?.clientId ||
       clientDetails.clientId ||
       clientDetails.client_id ||
       shiftDetails.clientId ||
      shiftDetails.client_id ||
      timesheetDetails.clientId ||
      timesheetDetails.client_id ||
      null,
    shiftId: fallback.shiftId || qrData?.shiftId || null,
    timesheetId: fallback.timesheetId || qrData?.timesheetId || null,
    direction: fallback.direction || qrData?.direction || "auto",
    shiftDate: fallback.shiftDate || clientInfo?.shiftDate || null,
    email:
      fallback.email ||
      clientDetails.email ||
      clientDetails.clientEmail ||
      timesheetDetails.email ||
      DEFAULT_EMAIL,
    clientName:
      fallback.clientName ||
      clientDetails.clientName ||
      clientInfo?.clientName ||
      userName ||
      "User",
    clientAddress:
      fallback.clientAddress ||
      clientDetails.clientAddress ||
      "",
    locationName:
      fallback.locationName ||
      shiftDetails.location ||
      timesheetDetails.location ||
      clientInfo?.location ||
      "",
    position:
      fallback.position ||
      shiftDetails.position ||
      timesheetDetails.position ||
      "",
    staffName:
      fallback.staffName ||
      timesheetDetails.staffName ||
      timesheetDetails.accountName ||
      "",
    startDate,
    endDate,
    dateLabel: fallback.dateLabel || formatDateLabel(startDate),
    clientDetails,
    shiftDetails,
    timesheetDetails,
  };
}

export function extractManageContext({ qrData, clientInfo, userName }) {
  return buildFromParts({ qrData, clientInfo, userName });
}

export function saveManageContext(context) {
  if (typeof window === "undefined") return;
  if (!context || !context.clientName) return;
  localStorage.setItem(MANAGE_CONTEXT_KEY, JSON.stringify(context));
}

export function clearManageContext() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(MANAGE_CONTEXT_KEY);
}

function getSelectedLocationsBucket() {
  if (typeof window === "undefined") return {};
  return safeParse(localStorage.getItem(MANAGE_SELECTED_LOCATIONS_KEY)) || {};
}

function setSelectedLocationsBucket(bucket) {
  if (typeof window === "undefined") return;
  localStorage.setItem(MANAGE_SELECTED_LOCATIONS_KEY, JSON.stringify(bucket));
}

function selectedLocationsBucketKey(clientId, clientDeviceId) {
  return `${clientId || ""}:${clientDeviceId || ""}`;
}

export function saveManageSelectedLocations({ clientId, clientDeviceId, selectedLocationIds }) {
  if (typeof window === "undefined") return;
  if (!clientId) return;
  const bucket = getSelectedLocationsBucket();
  const key = selectedLocationsBucketKey(clientId, clientDeviceId);
  bucket[key] = Array.isArray(selectedLocationIds) ? selectedLocationIds.map((id) => String(id)) : [];
  setSelectedLocationsBucket(bucket);
}

export function loadManageSelectedLocations({ clientId, clientDeviceId }) {
  if (typeof window === "undefined") return [];
  if (!clientId) return [];
  const bucket = getSelectedLocationsBucket();
  const key = selectedLocationsBucketKey(clientId, clientDeviceId);
  if (Array.isArray(bucket[key])) return bucket[key];

  const clientPrefix = `${clientId}:`;
  const matchingKey = Object.keys(bucket).find((entryKey) => entryKey.startsWith(clientPrefix));
  return matchingKey && Array.isArray(bucket[matchingKey]) ? bucket[matchingKey] : [];
}

/**
 * Check-in link with `locationId` (e.g. timesheet QR): add it to manageSelectedLocations for
 * this client/device. A registered kiosk (clientDeviceId) that already has locations for this
 * client keeps the ones chosen in Manage Device.
 */
export function addSelectedLocationFromCheckinUrl(searchString) {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(searchString || "");
  const clientId = String(params.get("clientId") || "").trim();
  const locationId = String(params.get("locationId") || "").trim();
  if (!clientId || !locationId) return;

  const stored = loadStoredManageContextRaw();
  const storedDeviceId =
    stored?.clientDeviceId && (!stored.clientId || String(stored.clientId) === clientId)
      ? String(stored.clientDeviceId)
      : "";
  const clientDeviceId = String(params.get("clientDeviceId") || "").trim() || storedDeviceId;

  const existing = loadManageSelectedLocations({ clientId, clientDeviceId }).map(String);
  if (clientDeviceId && existing.length > 0) return;
  if (existing.includes(locationId)) return;

  saveManageSelectedLocations({
    clientId,
    clientDeviceId,
    selectedLocationIds: [...existing, locationId],
  });
  console.log("[checkin] added URL location to manageSelectedLocations", {
    clientId,
    clientDeviceId,
    locationId,
  });
}

export function clearManageSelectedLocations({ clientId, clientDeviceId } = {}) {
  if (typeof window === "undefined") return;
  const bucket = getSelectedLocationsBucket();
  if (!clientId) {
    localStorage.removeItem(MANAGE_SELECTED_LOCATIONS_KEY);
    return;
  }
  const clientPrefix = `${clientId}:`;
  for (const key of Object.keys(bucket)) {
    if (key === selectedLocationsBucketKey(clientId, clientDeviceId) || key.startsWith(clientPrefix)) {
      delete bucket[key];
    }
  }
  setSelectedLocationsBucket(bucket);
}

/** Raw persisted object from localStorage only (no URL merge). For kiosk restore. */
export function loadStoredManageContextRaw() {
  if (typeof window === "undefined") return null;
  return safeParse(localStorage.getItem(MANAGE_CONTEXT_KEY));
}

export function loadManageContext(location) {
  const params = new URLSearchParams(location?.search || "");
  const stateContext = location?.state?.manageContext || null;
  const urlContext = {
    email: params.get("email") || null,
    clientName: params.get("name") || params.get("client") || null,
    accountId: params.get("accountId") || null,
    clientDeviceId: params.get("clientDeviceId") || null,
    browserDeviceGuid: params.get("browserDeviceGuid") || getBrowserDeviceGuid(),
    clientId: params.get("clientId") || null,
    contactRole: params.get("contactRole") || null,
    capabilities: safeParse(params.get("capabilities")) || null,
    shiftId: params.get("shiftId") || null,
    timesheetId: params.get("timesheetId") || null,
    locationName: params.get("location") || null,
    shiftDate: params.get("shiftDate") || params.get("from") || null,
    startDate: params.get("from") || params.get("shiftDate") || null,
    endDate: params.get("to") || params.get("from") || params.get("shiftDate") || null,
  };

  let storedContext = null;
  let cachedQrData = null;
  let storedManageUser = null;
  if (typeof window !== "undefined") {
    storedContext = safeParse(localStorage.getItem(MANAGE_CONTEXT_KEY));
    cachedQrData = safeParse(localStorage.getItem(QR_CACHE_KEY));
    storedManageUser = safeParse(localStorage.getItem("manageUser"));
  }

  // If the URL specifies a different client, discard stale storedContext/cachedQrData
  const urlClientId = params.get("clientId");
  const urlClientName = params.get("name") || params.get("client");
  if (storedContext) {
    const isDifferentClient =
      (urlClientId && storedContext.clientId && String(storedContext.clientId) !== String(urlClientId)) ||
      (urlClientName && storedContext.clientName && String(storedContext.clientName).toLowerCase() !== String(urlClientName).toLowerCase());

    if (isDifferentClient) {
      storedContext = null;
      cachedQrData = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem(MANAGE_CONTEXT_KEY);
        localStorage.removeItem(QR_CACHE_KEY);
      }
    }
  }

  // Cleanup polluted or un-scoped location IDs in existing context
  if (storedContext && storedContext.selectedLocationIds && storedContext.selectedLocationIds.length > 0) {
    const selectedClientId = storedContext.selectedLocationClientId || null;
    const selectedDeviceId = storedContext.selectedLocationDeviceId || null;
    const currentClientId = storedContext.clientId || null;
    const currentDeviceId = storedContext.clientDeviceId || null;
    const mismatchedClient =
      !selectedClientId || !currentClientId || String(selectedClientId) !== String(currentClientId);
    const mismatchedDevice =
      !selectedDeviceId ||
      !currentDeviceId ||
      String(selectedDeviceId) !== String(currentDeviceId);
    if (mismatchedClient || mismatchedDevice) {
      storedContext.selectedLocationIds = [];
      storedContext.selectedLocationClientId = null;
      storedContext.selectedLocationDeviceId = null;
    }
  }

  const mergedFallback = {
    ...(cachedQrData?.clientDetails?.clientName ? buildFromParts({ qrData: cachedQrData }) : {}),
    ...(storedContext || {}),
    ...(stateContext || {}),
    ...Object.fromEntries(
      Object.entries(urlContext).filter(([, value]) => value !== null && value !== ""),
    ),
  };

  const selectedLocParam = params.get("selectedLocationIds");
  if (selectedLocParam != null && String(selectedLocParam).trim() !== "") {
    mergedFallback.selectedLocationIds = String(selectedLocParam)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    mergedFallback.selectedLocationClientId = urlClientId || mergedFallback.clientId || null;
  }

  if (!mergedFallback.clientId && storedManageUser?.clientId) {
    mergedFallback.clientId = storedManageUser.clientId;
  }
  if (!mergedFallback.accountId && storedManageUser?.accountId) {
    mergedFallback.accountId = storedManageUser.accountId;
  }
  if (!mergedFallback.capabilities && storedManageUser?.capabilities) {
    mergedFallback.capabilities = storedManageUser.capabilities;
  }

  if (!mergedFallback.email) {
    mergedFallback.email = DEFAULT_EMAIL;
  }
  if (!mergedFallback.clientName) {
    mergedFallback.clientName = "User";
  }

  return buildFromParts({ fallback: mergedFallback });
}

function decodeUrlParam(value) {
  if (value == null || value === "") return "";
  try {
    return decodeURIComponent(String(value));
  } catch {
    return String(value);
  }
}

/** True when URL has kiosk check-in context (clientId + date + client label). */
export function hasKioskCheckinContext(searchString) {
  const params = new URLSearchParams(searchString || "");
  const clientId = params.get("clientId");
  const clientName = params.get("client") || params.get("name");
  const shiftDate = params.get("shiftDate") || params.get("from");
  return Boolean(clientId && clientName && shiftDate);
}

/** Build qrData-shaped object from kiosk URL params when no QR token is present. */
export function buildKioskQrDataFromSearch(searchString) {
  const params = new URLSearchParams(searchString || "");
  const clientId = params.get("clientId");
  const clientName = decodeUrlParam(params.get("client") || params.get("name"));
  const location = decodeUrlParam(params.get("location"));
  const shiftDate = params.get("shiftDate") || params.get("from");
  const startDate = params.get("from") || shiftDate;
  const endDate = params.get("to") || startDate;

  if (!clientId && !clientName) return null;

  return {
    clientDetails: {
      clientId: clientId ? Number(clientId) : null,
      clientName: clientName || "Unknown Client",
    },
    shiftDetails: {
      location: location || null,
      startDate: startDate || null,
      endDate: endDate || null,
    },
  };
}

/** Append kiosk scoping params to a URLSearchParams for check-shift-status. */
export function appendKioskShiftStatusParams(qs, searchString) {
  const params = new URLSearchParams(searchString || "");
  const clientId = params.get("clientId");
  const location = params.get("location");
  const shiftDate = getAppTodayIso();
  if (clientId) qs.set("clientId", String(clientId));
  if (location) qs.set("location", decodeUrlParam(location));
  if (shiftDate) qs.set("shiftDate", String(shiftDate));
  return qs;
}
